import { PUBLIC_CONTENTFUL_ENVIRONMENT } from '$env/static/public';
import type { INewsFields } from "$lib/utils/contentful/types";
import type { INewsFilters } from './types';
import { getMonthRange } from '$lib/utils/date';

/**
 * Get the news utils object
 * @param _fetch The fetch function
 * @returns The news utils object
 */
export const useNewsUtils = (_fetch: {
  (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
  (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
}): NewsUtils => {
  return new NewsUtils(_fetch);
};

export class NewsUtils {
  private static PAGE_DEFAULT_SIZE = 20;

  private static BASE_URI = 'https://cdn.contentful.com';
  private static SPACE_ID = 'fumyn2q16o4i';
  private static ACCESS_TOKEN = 'TRsNY4r-LgKqoW_EwZyjh0wI2Go0WxpIuV5xlsTpdHg';
  private static ENVIRONMENT_ID = PUBLIC_CONTENTFUL_ENVIRONMENT;
  private static CONTENT_TYPE = 'news';

  private static PREVIEW_BASE_URI = 'https://preview.contentful.com';
  private static PREVIEW_ACCESS_TOKEN = 'NkKR_ZRrbcUcvvbuz3iXe8ZRpjp-PtV2Kvs7pQB9qx4';

  private _fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }

  public constructor(fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }) {
    this._fetch = fetch;
  }

  public getRecentNews = async (): Promise<INewsFields[]> => {
    return await this._fetch(`/api/v1/news/recent.json`)
      .then((res) => res.json())
      .then((body) => {
        if (body) {
          return body;
        }
        return null;
      })
      .catch((e) => {
        console.log('News NOT Found.', e);
        return null;
      });
  };

  public getLastNews = async () => {
    let query = `${NewsUtils.BASE_URI}/spaces/${NewsUtils.SPACE_ID}/environments/${NewsUtils.ENVIRONMENT_ID}/entries?access_token=${NewsUtils.ACCESS_TOKEN}&content_type=${NewsUtils.CONTENT_TYPE}&limit=1&order=-fields.publishedDate&fields.newsVisibilities=Abilita la news sul sito`;

    const rawCollection = await this._fetch(query).then((res) => res.json());
    const entriesCollection = NewsUtils.resolveLinks(rawCollection);
    return entriesCollection.items[0];
  };

  public getNewsBySlug = async (
    slug: string,
    preview: boolean
  ) => {
    if (preview) {
      const query = `${NewsUtils.PREVIEW_BASE_URI}/spaces/${NewsUtils.SPACE_ID}/environments/${NewsUtils.ENVIRONMENT_ID}/entries?access_token=${NewsUtils.PREVIEW_ACCESS_TOKEN}&content_type=${NewsUtils.CONTENT_TYPE}&fields.slug=${slug}&fields.newsVisibilities=Abilita la news sul sito`;

      const rawCollection = await this._fetch(query).then((res) => res.json());

      if (rawCollection.items.length !== 0) {
        const entriesCollection = NewsUtils.resolveLinks(rawCollection);
        return entriesCollection.items[0];
      } else {
        return null;
      }
    } else {
      return await this._fetch(`/api/v1/news/sl/${slug}.json`)
        .then((res) => res.json())
        .then((body) => {
          if (body) {
            return body;
          }
          return null;
        })
        .catch((e) => {
          console.log('News NOT Found.', e);
          return null;
        });
    }
  };

  public getNews = async (
    page: number = 0,
    pageSize: number = 20,
    filters: INewsFilters | null = null,
    orderBy: string = 'publishedDate',
    withoutIds: string[] = []
  ) => {
    const limit = pageSize;
    const skip = page * pageSize;
    let queries = [];

    let query = `${NewsUtils.BASE_URI}/spaces/${NewsUtils.SPACE_ID}/environments/${NewsUtils.ENVIRONMENT_ID}/entries?access_token=${NewsUtils.ACCESS_TOKEN}&content_type=${NewsUtils.CONTENT_TYPE}&limit=${limit}&skip=${skip}&order=-fields.${orderBy}&fields.newsVisibilities=Abilita la news sul sito`;

    if (withoutIds.length > 0) {
      query += `&sys.id[nin]=${withoutIds.join(',')}`;
    }

    if (filters != null) {

      queries = [];
      let filterCategories = '';
      let filterMonths = '';

      if (filters.category.length > 0) {
        filterCategories = `&fields.tags[in]=${filters.category.join(',')}`;
      }
      query += filterCategories.replace(/ /g, '%20');

      if (filters.month.length > 0) {
        for (let i = 0; i < filters.month.length; i++) {
          filterMonths = '';
          let monthRange = getMonthRange(filters.month[i]);
          filterMonths = `&fields.publishedDate[gte]=${monthRange[0]}&fields.publishedDate[lte]=${monthRange[1]}`;
          queries.push(query + filterMonths);
        }
      }
    }

    if (queries.length == 0) {
      const rawCollection = await fetch(query).then((res) => res.json());
      const entriesCollection = NewsUtils.resolveLinks(rawCollection);
      return {
        news: entriesCollection.items,
        total: entriesCollection.total,
      };
    } else {
      let news = [];
      let total = 0;
      for (let i = 0; i < queries.length; i++) {
        const rawCollection = await this._fetch(queries[i]).then((res) => res.json());
        const entriesCollection = NewsUtils.resolveLinks(rawCollection);
        news = news.concat(entriesCollection.items);
        total += entriesCollection.total;
      }
      return {
        news: news,
        total: total,
      };
    }

  };

  private static resolveLinks(entriesCollection) {
    const copy = { ...entriesCollection };
    if (entriesCollection.includes && entriesCollection.includes.Asset) {
      const assets = entriesCollection.includes.Asset;
      copy.items.forEach((item) => {
        const fields = item.fields;
        for (const field in fields) {
          if (fields[field].sys && fields[field].sys.linkType === 'Asset') {
            const linkId = fields[field].sys.id;
            const linkedAsset = assets.find((asset) => asset.sys.id === linkId);
            fields[field] = { ...linkedAsset };
          }
        }
      });
      delete copy.includes;
      return copy;
    }
    return entriesCollection;
  }
}